import TenantQualifications from "shared/icons/tenantQualifications.svg?react";
import { cx } from "shared/utils";
import { ApplicantType, ApplicationStatus } from "shared/types";
import { VerificationSchemaView } from "../../slices/types";
import { LeftPane } from "./LeftPane";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Hide,
  Show,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import {
  Award03,
  CurrencyDollar,
  FileCheck02,
  Grid01,
  Mail02,
  Settings02
} from "untitledui-js-base";
import { useTemplateContext } from "../../provider";
import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Template } from "../../../../../types/landlord/verificationTemplate";
import { TemplateMenu } from "../TemplateMenu";
import { screeningServicesTabs } from "../../constant";
import {
  ArrowLeft02Icon,
  DiscountTag02Icon,
  SidebarLeftIcon,
  SidebarRightIcon,
  SparklesIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { ChangeTemplateSubmenu } from "../ChangeTemplateSubmenu";
import { TemplateRouteSwitcher } from "./TemplateRouteSwitcher";
import { useAuthContext } from "providers/Auth";
import { Link, useNavigate } from "react-router-dom";
import { RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import templatesApi from "api/landlord/templates";
import { PawPrintIcon } from "shared/icons/hugeicons-corrected/icons/paw-print-stroke";
import { useUnderwritingAgentsEnabled } from "shared/app/UnderwritingAgents/underwritingAgentsFlag";

const UnderwritingAgentPane: FC<{ onClick: () => void }> = ({ onClick }) => {
  const isUnderwritingAgentsEnabled = useUnderwritingAgentsEnabled();
  const { template, createMode, selectedVerificationSchema } =
    useTemplateContext();
  const isSelected =
    selectedVerificationSchema === VerificationSchemaView.UnderwritingAgent;

  return (
    // An application's copy of its template comes without the agent, so there is nothing to show
    // there; the Create template window always shows the entry, disabled.
    <Show
      if={
        isUnderwritingAgentsEnabled &&
        (createMode || template?.underwriting_agent !== undefined)
      }
    >
      <LeftPane
        onClick={onClick}
        tabName={VerificationSchemaView.UnderwritingAgent}
        selectedTab={selectedVerificationSchema}
        Icon={
          <HugeiconsIcon
            icon={SparklesIcon}
            size={20}
            className={cx(
              "stroke-content-500",
              isSelected && "stroke-primary-500 text-primary-600"
            )}
          />
        }
        disabled={createMode}
        disabledMessage="To choose an underwriting agent, first create a template"
        newUntilDate="2027-01-31"
      />
    </Show>
  );
};

interface EditableNameProps {
  readOnly: boolean;
  error?: string;
  editedName: string;
  setEditedName?: (x: string) => void;
  clearError?: () => void;
  toggleEditMode?: () => void;
  inputRef: RefObject<HTMLTextAreaElement>;
}

const EditableName: FC<EditableNameProps> = ({
  readOnly,
  error,
  editedName,
  setEditedName,
  clearError,
  toggleEditMode,
  inputRef
}) => {
  if (readOnly)
    return (
      <h1 className="font-bold text-content-1000 text-lg break-words">
        {editedName}
      </h1>
    );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!setEditedName) return;
    setEditedName(e.target.value);
    clearError && clearError();
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    if (toggleEditMode) toggleEditMode();
    const input = e.target;
    const length = input.value.length;
    requestAnimationFrame(() => {
      input.setSelectionRange(length, length);
    });
  };

  const handleBlur = () => {
    if (toggleEditMode) toggleEditMode();
  };

  return (
    <textarea
      ref={inputRef}
      rows={1}
      className={cx(
        "block w-full resize-none overflow-hidden break-words border-none bg-transparent p-0 text-lg font-bold focus:outline-none focus:ring-0",
        error
          ? "text-danger-600 placeholder:text-danger-600"
          : "text-content-1000 hover:text-content-400 focus:hover:text-content-1000"
      )}
      onChange={handleChange}
      onInput={(e) => fitToContent(e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        e.currentTarget.blur();
      }}
      value={editedName}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder="Untitled"
      readOnly={readOnly}
    />
  );
};

const stickyHeaderClassName = (scrolled: boolean) =>
  cx(
    "sticky -top-4 z-10 -mx-4 -mt-4 border-b bg-white px-4 pb-3 pt-4 transition-shadow",
    scrolled ? "border-alpha-100 shadow-md" : "border-transparent"
  );

const fitToContent = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
};

interface Props {
  isModal?: boolean;
  error?: string;
  clearError?: () => void;
  isEditing?: boolean;
  template?: Template;
  setEditedName?: (x: string) => void;
  editedName: string;
  toggleEditMode?: () => void;
  handleDuplicate?: () => void;
  setShowDeleteModal?: (v: boolean) => void;
  applicationId?: string;
  applicationStatus?: ApplicationStatus;
  showBackButton?: boolean;
  backLink?: string;
  propertyId?: number;
}

export const SideBar: FC<Props> = ({
  isModal,
  error,
  clearError,
  template,
  editedName,
  setEditedName,
  toggleEditMode,
  handleDuplicate,
  setShowDeleteModal,
  applicationId,
  applicationStatus,
  showBackButton,
  backLink = "/verification-templates",
  propertyId
}) => {
  const navigate = useNavigate();
  const {
    readOnly,
    createMode,
    partnerType,
    setPartnerType,
    setSelectedVerificationSchema,
    selectedVerificationSchema
  } = useTemplateContext();
  const {
    type: { hasTemplateFeeCollection }
  } = useAuthContext();
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const parentTemplateId = template?.parent_template_identifier;
  const parentTemplateQuery = useQuery({
    queryKey: ["VERIFICATION_TEMPLATE", parentTemplateId],
    queryFn: () => templatesApi.getTemplate(parentTemplateId || ""),
    enabled: Boolean(parentTemplateId)
  });

  const selectTab = (tabName: ApplicantType) => {
    setSelectedVerificationSchema(VerificationSchemaView.ScreeningServices);
    setPartnerType(tabName);
    setOverlayOpen(false);
  };

  const selectVerificationSchema = (v: VerificationSchemaView) => {
    setSelectedVerificationSchema(v);
    setOverlayOpen(false);
  };

  useEffect(() => {
    if (error && editInputRef.current) editInputRef.current.focus();
  }, [error]);

  useLayoutEffect(() => {
    if (editInputRef.current) fitToContent(editInputRef.current);
  }, [editedName]);

  return (
    <>
      <div className="vt-sidebar-strip">
        <IconButton
          size={IconButtonSize.SM}
          variant={IconButtonVariant.BASE}
          borderLess
          HugeIcon={SidebarLeftIcon}
          onClick={() => setOverlayOpen(true)}
        />
      </div>
      <Show if={overlayOpen}>
        <div
          onClick={() => setOverlayOpen(false)}
          className={cx(
            "vt-sidebar-backdrop",
            isModal && "vt-sidebar-backdrop--modal"
          )}
        />
      </Show>
      <div
        className={cx(
          "vt-sidebar hide-scroll",
          overlayOpen && "vt-sidebar--open",
          isModal && "vt-sidebar--modal"
        )}
        onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 0)}
      >
        <div className={stickyHeaderClassName(scrolled)}>
          <Show if={showBackButton}>
            <div>
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.XS}
                HugeIcon={ArrowLeft02Icon}
                onClick={() => navigate(backLink)}
                className="!gap-2 !text-content-1000"
              >
                Templates
              </Button>
            </div>
            <div className="my-4 border-b border-alpha-40" />
          </Show>
          <div className="flex justify-between items-start gap-2 py-2">
            <div className="min-w-0 flex-1 space-y-2">
              <p
                className={cx(
                  "text-sm",
                  error ? "text-danger-600" : "text-content-400"
                )}
              >
                {parentTemplateId ? (
                  <>
                    Variant of{" "}
                    <Link
                      to={`/verification-templates/${parentTemplateId}`}
                      className="font-medium text-primary-600 underline"
                    >
                      {parentTemplateQuery.data?.verification_template.name ??
                        "parent template"}
                    </Link>
                  </>
                ) : (
                  "Screening template"
                )}
              </p>
              <EditableName
                readOnly={readOnly}
                error={error}
                editedName={editedName}
                setEditedName={setEditedName}
                clearError={clearError}
                toggleEditMode={toggleEditMode}
                inputRef={editInputRef}
              />
            </div>

            <div className="flex items-center gap-2">
              <Hide if={createMode}>
                <TemplateMenu
                  template={template}
                  onDelete={
                    setShowDeleteModal
                      ? () => setShowDeleteModal(true)
                      : undefined
                  }
                  onDuplicate={handleDuplicate}
                />
              </Hide>
              <div className="vt-sidebar-close">
                <IconButton
                  size={IconButtonSize.SM}
                  variant={IconButtonVariant.BASE}
                  HugeIcon={SidebarRightIcon}
                  onClick={() => setOverlayOpen(false)}
                />
              </div>
            </div>
          </div>
        </div>

        <Hide if={Boolean(applicationId)}>
          {template ? (
            <TemplateRouteSwitcher
              template={template}
              parentTemplate={parentTemplateQuery.data?.verification_template}
            />
          ) : null}
        </Hide>

        <Hide if={!applicationId}>
          <ChangeTemplateSubmenu
            applicationId={applicationId || ""}
            applicationStatus={applicationStatus}
            currentTemplate={template || undefined}
            propertyId={propertyId}
          />
        </Hide>

        <div className="my-4 border-b border-alpha-40" />
        <div className="space-y-1 max-h-[calc(100vh-220px)] overflow-y-auto">
          <LeftPane
            onClick={(v) => selectTab(v as ApplicantType)}
            tabName={VerificationSchemaView.ScreeningServices}
            active={screeningServicesTabs.includes(selectedVerificationSchema)}
            selectedTab={selectedVerificationSchema}
            selectedChild={partnerType}
            Icon={<Grid01 size="20" className="stroke-content-500" />}
            childItems={[
              ApplicantType.Applicant,
              ApplicantType.CoSigner,
              ApplicantType.Occupant
            ]}
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.DecisionCriteria)
            }
            tabName={VerificationSchemaView.DecisionCriteria}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Award03
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.DecisionCriteria &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
            disabled={createMode}
            disabledMessage="To configure screening criteria, first create a template"
          />
          <UnderwritingAgentPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.UnderwritingAgent)
            }
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(
                VerificationSchemaView.TenantQualifications
              )
            }
            tabName={VerificationSchemaView.TenantQualifications}
            selectedTab={selectedVerificationSchema}
            Icon={<TenantQualifications />}
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(
                VerificationSchemaView.SubmissionDisclosure
              )
            }
            tabName={VerificationSchemaView.SubmissionDisclosure}
            selectedTab={selectedVerificationSchema}
            Icon={
              <FileCheck02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.SubmissionDisclosure &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.DiscountCodes)
            }
            tabName={VerificationSchemaView.DiscountCodes}
            selectedTab={selectedVerificationSchema}
            Icon={
              <HugeiconsIcon
                icon={DiscountTag02Icon}
                size={20}
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.DiscountCodes &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <Show if={hasTemplateFeeCollection}>
            <LeftPane
              onClick={() =>
                selectVerificationSchema(VerificationSchemaView.AdditionalFees)
              }
              tabName={VerificationSchemaView.AdditionalFees}
              selectedTab={selectedVerificationSchema}
              Icon={
                <CurrencyDollar
                  size="20"
                  className={cx(
                    "stroke-content-500",
                    selectedVerificationSchema ===
                      VerificationSchemaView.AdditionalFees &&
                      "stroke-primary-500 text-primary-600"
                  )}
                />
              }
            />
          </Show>
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.AdverseAction)
            }
            tabName={VerificationSchemaView.AdverseAction}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Mail02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.AdverseAction &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.PetPolicy)
            }
            tabName={VerificationSchemaView.PetPolicy}
            selectedTab={selectedVerificationSchema}
            Icon={
              <HugeiconsIcon
                size="20"
                icon={PawPrintIcon}
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.PetPolicy &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
            newUntilDate="2026-10-15"
          />
          <LeftPane
            onClick={() =>
              selectVerificationSchema(VerificationSchemaView.General)
            }
            tabName={VerificationSchemaView.General}
            selectedTab={selectedVerificationSchema}
            Icon={
              <Settings02
                size="20"
                className={cx(
                  "stroke-content-500",
                  selectedVerificationSchema ===
                    VerificationSchemaView.General &&
                    "stroke-primary-500 text-primary-600"
                )}
              />
            }
          />
        </div>
      </div>
    </>
  );
};
